import React, { useState } from 'react';
import { StarIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function HondurasFilterPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-704">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-705">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-706">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-707">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-708" />
            <span data-id="element-709">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-710"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-711">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-712">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-713">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-714">
              The Filter Missionaries Trust When There's No Store to Buy One
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-715">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-716" />
            <div className="text-left space-y-1" data-id="element-717">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-718">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-719">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-720">
                May 5, 2026 · 5 min read
              </p>
            </div>
          </div>

          {/* Image 1 */}
          <div className="w-full aspect-[16/9] overflow-hidden rounded-lg shadow-md bg-gray-100" data-id="element-721">
            <img src="https://cdn.magicpatterns.com/uploads/79Eh5Jb5wXwFkK5WfedeUD/homduras-filter-image-1.webp" alt="Missionary water project" className="w-full h-full object-cover" data-id="element-722" />
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-723">
            <p data-id="element-724">
              By the time most American families think about their water,
              they're standing in a store aisle comparing $300 countertop units,
              reading boxes that promise "advanced filtration" and a cartridge
              you'll be reordering by Christmas.
            </p>
            <p data-id="element-725">Missionaries skipped that aisle a long time ago.</p>
            <p data-id="element-726">
              Not because they couldn't afford the fancy units. Because they
              learned — the hard way, in places where a failed water supply
              isn't an inconvenience but a funeral — that the filter you can't
              rebuild yourself is the filter that fails you when it matters
              most.
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-727">
              Why Missionaries Skipped the Filter Aisle
            </h2>

            <p data-id="element-728">
              In places like rural Honduras, the nearest hardware store can be a
              day's drive over roads that wash out in the rainy season. The
              power flickers more than it holds. A replacement cartridge ordered
              online might arrive in three weeks — or never.
            </p>
            <p data-id="element-729">
              In that world, a $300 filter with a proprietary cartridge isn't a
              solution. It's a countdown timer. The day the cartridge clogs, it
              becomes a plastic brick.
            </p>
            <p data-id="element-730">
              So the water teams that have worked Central America for decades
              brought something older and far more stubborn:{' '}
              <b data-id="element-731">a barrel, layers of gravel and sand, and a little patience.</b>
            </p>
            <p data-id="element-732">
              No electricity. No replacement filters. No permits. Just gravity
              pulling dirty water down through sand — and a process most people
              have never heard of doing the real work.
            </p>

            {/* Image 2 */}
            <div className="w-full aspect-[16/9] overflow-hidden rounded-lg shadow-md bg-gray-100 my-2" data-id="element-733">
              <img src="https://cdn.magicpatterns.com/uploads/phzBs5JkiPgGJ8c7cQh1sk/homduras-filter-image-2.webp" alt="Barrel of clean water" className="w-full h-full object-cover" data-id="element-734" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-735">
              The Part the Filter Brands Won't Print on the Box
            </h2>

            <p data-id="element-736">Here's what almost nobody tells you about a sand filter.</p>
            <p data-id="element-737">
              For the first couple of weeks, it's only straining the water —
              catching the big stuff, like a very fine net. That's the part
              people understand.
            </p>
            <p data-id="element-738">Then something else happens.</p>
            <p data-id="element-739">
              A living layer forms across the top of the sand. Call it what the
              engineers call it, or call it what it is:{' '}
              <b data-id="element-740">
                millions of microorganisms that move in, settle down, and start
                eating the bacteria out of the water before it ever reaches the
                bottom of the barrel.
              </b>
            </p>
            <p data-id="element-741">
              The filter stops merely straining water and starts cleaning it
              biologically — the same way a healthy riverbank cleans a stream.
              Living things doing the work that no cartridge can.
            </p>
            <p data-id="element-742">
              And here's the part that turns everything you know about filters
              upside down: <b data-id="element-743">it gets better the longer it runs, not worse.</b>{' '}
              A store-bought cartridge is at its best the day you install it and
              degrades from there. This does the opposite. Leave it running,
              keep it fed, and the colony matures into something more effective
              than anything you can buy in a box.
            </p>

            {/* Image 3 */}
            <div className="w-full aspect-[16/9] overflow-hidden rounded-lg shadow-md bg-gray-100 my-2" data-id="element-744">
              <img src="https://cdn.magicpatterns.com/uploads/pMF7tYcdXnTxFM2BcqJZKi/homduras-filter-image-3.webp" alt="Sand layers detail" className="w-full h-full object-cover" data-id="element-745" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-746">
              The Detail That Separates a Real Filter From a Science Project
            </h2>

            <p data-id="element-747">
              There's one mistake that quietly kills these filters, and it's the
              reason a lot of DIY versions on the internet don't actually work.
            </p>
            <p data-id="element-748">
              If the sand is allowed to dry out between uses,{' '}
              <b data-id="element-749">that living layer dies</b>. You're back to a glorified strainer
              — mechanical cleaning only, none of the biology.
            </p>
            <p data-id="element-750">
              The missionaries who got this right understood that the water has
              to stay above the sand at all times, even when the filter isn't in
              use. Keep the bed submerged, and the colony lives. Let it run dry,
              and you've built a science project, not a survival tool.
            </p>
            <p data-id="element-751">
              It's a small thing. It's also the difference between water you can
              trust and water you can't. Most people never learn it until their
              filter has already failed them.
            </p>

            {/* Image 4 */}
            <div className="w-full aspect-[16/9] overflow-hidden rounded-lg shadow-md bg-gray-100 my-2" data-id="element-752">
              <img src="https://cdn.magicpatterns.com/uploads/ne8o6rLTagji4sq9krvfye/homduras-filter-image-4.webp" alt="Water bed maintenance" className="w-full h-full object-cover" data-id="element-753" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-754">
              $150 Once vs. $300 Every Six Months
            </h2>

            <p data-id="element-755">Run the math the way a missionary on a tight budget has to.</p>
            <p data-id="element-756">
              <b data-id="element-757">A store-bought cartridge system:</b> $300 up front, then
              another cartridge every six months for as long as you own it. Miss
              a reorder, or lose the supply chain, and it stops.
            </p>
            <p data-id="element-758">
              <b data-id="element-759">The barrel:</b> roughly $150 in materials, built one time, from
              parts you can buy at any hardware store — or scavenge if you have
              to. The families those water teams set up years ago are still
              drawing from the same barrels today.
            </p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-760">
              That's the difference between buying preparedness and building it.
              One depends on a company staying in business and a truck showing
              up. The other depends on you.
            </blockquote>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-761">
              Why This Spread Through Mission Fields, Not Big-Box Stores
            </h2>

            <p data-id="element-762">
              There's a reason you find this method in mission fields and not on
              store shelves.
            </p>
            <p data-id="element-763">
              A store needs you to come back. A filter that runs on sand,
              gravity, and time doesn't sell anybody a refill, a subscription,
              or a "filter replacement reminder" email.
            </p>
            <p data-id="element-764">
              The people who depend on it most are the ones who can least afford
              to depend on a supply chain — and that turns out to be exactly the
              kind of independence a lot of American families are quietly
              starting to want for themselves.
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-765">
              When the Trucks Stop Coming
            </h2>

            <p data-id="element-766">
              A storm. A grid failure. A boil-water advisory that lasts a week
              instead of a day. Or something worse.
            </p>
            <p data-id="element-767">
              When that day comes, the family that already knows how to turn
              river, rain, or pond water into something safe isn't standing in
              any aisle, refreshing a delivery tracking page.
            </p>
            <p className="font-bold text-[#0f172a]" data-id="element-768">
              They're already drinking.
            </p>

            {/* Image 5 */}
            <div className="w-full aspect-[16/9] overflow-hidden rounded-lg shadow-md bg-gray-100 my-2" data-id="element-769">
              <img src="https://cdn.magicpatterns.com/uploads/gfpZw3nMcQLaNQweV44Mcz/homduras-filter-image-5.webp" alt="Family with clean water" className="w-full h-full object-cover" data-id="element-770" />
            </div>

            <p data-id="element-771">
              The materials cost less than a tank of gas. The knowledge costs
              nothing but the willingness to learn it before you need it —
              which, when you think about it, is the whole game.
            </p>
            <p className="font-bold text-[#0f172a]" data-id="element-772">
              Build it before you need it.
            </p>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-773">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-774">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-775">
            <div className="relative w-10 h-10" data-id="element-776">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-777" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-778" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-779">
            <p data-id="element-780">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-781">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-782">
                Terms & Conditions
              </button>
              <span data-id="element-783">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-784">
                Privacy Policy
              </button>
              <span data-id="element-785">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-786">
                Contact Us
              </button>
            </p>
            <p data-id="element-787">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-788">
          <div className="absolute inset-0 bg-black/50" data-id="element-789" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-790">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-791">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-792">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-793">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-794" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-795">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-796">
                    Last Updated: May 2026
                  </p>
                  <p data-id="element-797">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                  </p>
                </>}
              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-798">
                    Last Updated: May 2026
                  </p>
                  <p data-id="element-799">
                    PatriotsInformer Media, LLC is committed to protecting your
                    privacy.
                  </p>
                </>}
              {activeModal === 'contact' && <>
                  <p data-id="element-800">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-801">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-802">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-803">
                      <span className="font-semibold text-[#0f172a]" data-id="element-804">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-805">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}